/**
 * Account UI: first-run, the arcade initials entry and the account sheet.
 *
 * A player's name is three arcade initials, picked on three letter
 * wheels like a cabinet high-score table. There is no free-text name,
 * no picture and no profile. First-run offers two co-equal doors,
 * "Sign in with Nostr" (a convenience on this phone only) and "Play as
 * guest", and both end at the same initials screen.
 */

import {
  DEFAULT_INITIALS, clearAccount, createGuestAccount, isInitials, loadInitials, saveInitials,
  type Account,
} from './identity.js';
import { getSignetAppOrigin, setSignetAppOrigin, signIn, signetLogout } from './signet.js';
import { setName as syncTournamentName } from './tournament.js';

const ALPHABET_SIZE = 26;
const A_CODE = 'A'.charCodeAt(0);
const WHEELS = 3;

/** Stable colour for a set of initials. */
function colourForInitials(initials: string): string {
  let h = 0;
  for (let i = 0; i < initials.length; i++) h = (h * 31 + initials.charCodeAt(i)) % 360;
  return `hsl(${h} 70% 62%)`;
}

export interface InitialsPicker {
  readonly element: HTMLElement;
  /** The three letters currently showing. */
  value(): string;
  /** Move keyboard focus to the first wheel. */
  focus(): void;
}

/**
 * Three letter wheels, A to Z, each with an up and a down button. Up
 * moves to the next letter and Z wraps to A; down moves back and A wraps
 * to Z. Each letter is also a keyboard-operable spin button: arrow keys
 * step it, a typed letter sets it and moves on to the next wheel.
 */
export function createInitialsPicker(start: string, onChange?: (initials: string) => void): InitialsPicker {
  const startLetters = (isInitials(start) ? start : DEFAULT_INITIALS).split('').map((c) => c.charCodeAt(0) - A_CODE);

  const element = document.createElement('div');
  element.className = 'initials';
  element.setAttribute('role', 'group');
  element.setAttribute('aria-label', 'Your three arcade initials');

  const live = document.createElement('div');
  live.className = 'visually-hidden';
  live.setAttribute('role', 'status');
  live.setAttribute('aria-live', 'polite');

  interface Wheel { n: number; readonly el: HTMLElement }
  const wheels: Wheel[] = [];
  const current = (): string => wheels.map((w) => String.fromCharCode(A_CODE + w.n)).join('');

  const paint = (w: Wheel): void => {
    const letter = String.fromCharCode(A_CODE + w.n);
    w.el.textContent = letter;
    w.el.setAttribute('aria-valuenow', String(w.n));
    w.el.setAttribute('aria-valuetext', letter);
  };
  const set = (w: Wheel, value: number): void => {
    w.n = ((value % ALPHABET_SIZE) + ALPHABET_SIZE) % ALPHABET_SIZE;
    paint(w);
    live.textContent = `Initials ${current().split('').join(' ')}`;
    onChange?.(current());
  };

  for (let i = 0; i < WHEELS; i++) {
    const w: Wheel = { n: startLetters[i] ?? 0, el: document.createElement('div') };
    wheels.push(w);
    const wheel = document.createElement('div');
    wheel.className = 'wheel';

    const makeButton = (dir: 1 | -1): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `wheel-btn ${dir === 1 ? 'up' : 'down'}`;
      b.setAttribute('aria-label', `Letter ${i + 1} ${dir === 1 ? 'up' : 'down'}`);
      b.textContent = dir === 1 ? '▲' : '▼';
      b.addEventListener('click', () => set(w, w.n + dir));
      return b;
    };

    const letter = w.el;
    letter.className = 'wheel-letter';
    letter.tabIndex = 0;
    letter.setAttribute('role', 'spinbutton');
    letter.setAttribute('aria-label', `Letter ${i + 1} of ${WHEELS}`);
    letter.setAttribute('aria-valuemin', '0');
    letter.setAttribute('aria-valuemax', String(ALPHABET_SIZE - 1));
    letter.addEventListener('keydown', (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'ArrowUp') {
        e.preventDefault(); set(w, w.n + 1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault(); set(w, w.n - 1);
      } else if (e.key === 'Home') {
        e.preventDefault(); set(w, 0);
      } else if (e.key === 'End') {
        e.preventDefault(); set(w, ALPHABET_SIZE - 1);
      } else if (e.key === 'Backspace') {
        e.preventDefault(); wheels[Math.max(0, i - 1)]?.el.focus();
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
        set(w, e.key.toUpperCase().charCodeAt(0) - A_CODE);
        wheels[i + 1]?.el.focus();
      }
    });

    wheel.append(makeButton(1), letter, makeButton(-1));
    element.appendChild(wheel);
    paint(w);
  }
  element.appendChild(live);

  return {
    element,
    value: current,
    focus: () => wheels[0]?.el.focus(),
  };
}

/**
 * Initials entry screen. Used on first run after either door, and once
 * for a phone whose older account has no initials yet. Replaces the
 * root's contents; `onDone` gets the saved initials.
 */
export function renderInitialsEntry(
  root: HTMLElement,
  onDone: (initials: string) => void,
  opts: { onBack?: () => void } = {},
): void {
  const screen = document.createElement('div');
  screen.className = 'firstrun';
  root.replaceChildren(screen);

  const heading = document.createElement('h1');
  heading.className = 'firstrun-heading';
  heading.textContent = 'ENTER YOUR INITIALS';

  const blurb = document.createElement('p');
  blurb.className = 'firstrun-blurb';
  blurb.textContent = 'Three letters, like an arcade high-score table. This is the only name the game sees.';

  const picker = createInitialsPicker(loadInitials() ?? DEFAULT_INITIALS);

  const start = document.createElement('button');
  start.type = 'button';
  start.className = 'btn primary firstrun-start';
  start.textContent = 'START PLAYING';
  start.addEventListener('click', () => {
    const initials = picker.value();
    if (!saveInitials(initials)) return;
    try { syncTournamentName(initials); } catch { /* ignore */ }
    onDone(initials);
  });

  if (opts.onBack) {
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'firstrun-back';
    back.textContent = '‹ Back';
    back.addEventListener('click', opts.onBack);
    screen.append(back);
  }
  screen.append(heading, blurb, picker.element, start);
  setTimeout(() => picker.focus(), 50);
}

/**
 * First-run: pick a door, then enter initials. Replaces the root's
 * contents and ends with an Account and saved initials.
 */
export function renderFirstRun(root: HTMLElement, onDone: (account: Account) => void): void {
  const screen = document.createElement('div');
  screen.className = 'firstrun';

  let done = false;
  const toInitials = (account: Account): void => {
    renderInitialsEntry(root, () => {
      if (done) return;
      done = true;
      onDone(account);
    }, { onBack: showDoors });
  };

  function showDoors(): void {
    root.replaceChildren(screen);
    const logo = document.createElement('div');
    logo.className = 'firstrun-logo';
    logo.textContent = 'JOYSTICK';

    const heading = document.createElement('h1');
    heading.className = 'firstrun-heading';
    heading.textContent = 'JOIN THE GAME';

    // Door 1: sign in. Most players already have a Nostr account.
    const signInCard = document.createElement('button');
    signInCard.type = 'button';
    signInCard.className = 'firstrun-door primary';
    signInCard.innerHTML = `
      <span class="firstrun-door-title">SIGN IN WITH NOSTR</span>
      <span class="firstrun-door-sub">Extension, bunker, Signet or key</span>
    `;
    signInCard.addEventListener('click', () => {
      signInCard.disabled = true;
      signInCard.classList.add('busy');
      void signIn().then((account) => {
        if (account) { toInitials(account); return; }
        // Cancelled or failed: let them try again.
        signInCard.disabled = false;
        signInCard.classList.remove('busy');
      });
    });

    // Door 2: guest. Jargon-free for non-Nostr family members.
    const guestCard = document.createElement('button');
    guestCard.type = 'button';
    guestCard.className = 'firstrun-door';
    guestCard.innerHTML = `
      <span class="firstrun-door-title">PLAY AS GUEST</span>
      <span class="firstrun-door-sub">Just pick three letters, we sort the rest</span>
    `;
    guestCard.addEventListener('click', () => toInitials(createGuestAccount()));

    // Advanced: point sign-in at a self-hosted signet-app. Collapsed
    // so it never clutters the simple path.
    const advToggle = document.createElement('button');
    advToggle.type = 'button';
    advToggle.className = 'firstrun-adv-toggle';
    advToggle.textContent = 'Advanced';
    const advPanel = document.createElement('div');
    advPanel.className = 'firstrun-adv';
    advPanel.style.display = 'none';
    const advLabel = document.createElement('label');
    advLabel.className = 'firstrun-adv-label';
    advLabel.textContent = 'SIGNET APP ORIGIN';
    const advInput = document.createElement('input');
    advInput.type = 'url';
    advInput.className = 'firstrun-input small';
    advInput.placeholder = 'https://your-signet.example';
    advInput.value = getSignetAppOrigin();
    advInput.setAttribute('autocomplete', 'off');
    advInput.spellcheck = false;
    advInput.addEventListener('change', () => setSignetAppOrigin(advInput.value));
    advPanel.appendChild(advLabel);
    advPanel.appendChild(advInput);
    advToggle.addEventListener('click', () => {
      advPanel.style.display = advPanel.style.display === 'none' ? 'flex' : 'none';
    });

    screen.replaceChildren(logo, heading, signInCard, guestCard, advToggle, advPanel);
  }

  showDoors();
}

/**
 * Account sheet: change initials, see how this phone signed in, switch
 * account. `onChange` fires after the initials change so the caller can
 * re-send the phone context.
 */
export function openProfileSheet(account: Account, onChange: (initials: string) => void): void {
  document.getElementById('profile-sheet')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'profile-sheet';
  overlay.className = 'sheet-overlay';
  const sheet = document.createElement('div');
  sheet.className = 'sheet';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'sheet-close';
  close.setAttribute('aria-label', 'close');
  close.textContent = '✕';
  const dismiss = (): void => overlay.remove();
  close.addEventListener('click', dismiss);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) dismiss(); });

  const title = document.createElement('div');
  title.className = 'sheet-title';
  title.textContent = 'YOUR ACCOUNT';

  const label = document.createElement('div');
  label.className = 'sheet-field-label';
  label.textContent = 'INITIALS';

  const picker = createInitialsPicker(loadInitials() ?? DEFAULT_INITIALS);

  const save = document.createElement('button');
  save.type = 'button';
  save.className = 'btn primary';
  save.textContent = 'SAVE';
  save.addEventListener('click', () => {
    const next = picker.value();
    if (next !== loadInitials() && saveInitials(next)) onChange(next);
    dismiss();
  });

  sheet.append(close, title, label, picker.element, save);

  if (account.source === 'signet') {
    const methodNote = document.createElement('div');
    methodNote.className = 'sheet-note';
    methodNote.textContent = `Signed in via ${account.method ?? 'Nostr'}`;
    sheet.append(methodNote);
  }

  const switchBtn = document.createElement('button');
  switchBtn.type = 'button';
  switchBtn.className = 'sheet-link danger';
  switchBtn.textContent = 'Switch account';
  switchBtn.addEventListener('click', () => {
    const warn = account.source === 'signet'
      ? 'Switch account? This signs you out on this phone and forgets your initials.'
      : 'Switch account? This phone forgets your initials.';
    if (!confirm(warn)) return;
    clearAccount();
    const reload = (): void => window.location.assign('/');
    if (account.source === 'signet') void signetLogout().then(reload, reload);
    else reload();
  });
  sheet.append(switchBtn);

  overlay.appendChild(sheet);
  (document.getElementById('app') ?? document.body).appendChild(overlay);
}

/**
 * A tappable chip showing the player's initials, mounted on the scanner
 * home and the waiting screen. Tapping opens the account sheet.
 *
 * `onAfterChange` runs after the initials change so the embedding screen
 * can re-send the phone context to a live game; the tournament-cookie
 * sync is handled here so every caller gets it for free.
 */
export function renderProfileChip(host: HTMLElement, account: Account, onAfterChange?: () => void): void {
  const initials = loadInitials() ?? DEFAULT_INITIALS;

  const chip = document.createElement('button');
  chip.type = 'button';
  chip.className = 'profile-chip';
  chip.setAttribute('aria-label', `Player ${initials.split('').join(' ')}. Change initials`);

  const dot = document.createElement('span');
  dot.className = 'profile-chip-dot';
  dot.style.background = colourForInitials(initials);

  const nameEl = document.createElement('span');
  nameEl.className = 'profile-chip-name';
  nameEl.textContent = initials;

  const edit = document.createElement('span');
  edit.className = 'profile-chip-edit';
  edit.textContent = 'EDIT';

  chip.append(dot, nameEl, edit);
  chip.addEventListener('click', () => {
    openProfileSheet(account, (next) => {
      nameEl.textContent = next;
      dot.style.background = colourForInitials(next);
      chip.setAttribute('aria-label', `Player ${next.split('').join(' ')}. Change initials`);
      try { syncTournamentName(next); } catch { /* ignore */ }
      onAfterChange?.();
    });
  });
  host.appendChild(chip);
}
